import { createContext, ReactNode, useContext, useEffect, useRef, useState } from 'react';
import { getFuseTag } from 'utilities/fuse.js';
import { DataDogVitals } from 'utilities/helpers/dataDog.vitals.js';
import { PageChangeContext } from 'utilities/hooks/page-change-context.js';

type UseAdPageReady = {
  bannerAdReady: boolean;
  setBannerAdReady: (adReady: boolean) => void;
};

export const AdPageInitContext = createContext<UseAdPageReady>({
  bannerAdReady: false,
  setBannerAdReady: () => 0,
});

/**
 * Calls ad page reset when pageUrlhasChanged and BannerConfig has populated
 * Fixes bug where fuse was refreshing before banner has updated its attributes
 * Causing ad to populate on old attributes
 */
export const AdPageInitProvider = ({
  children,
  rendersBannerAd,
}: {
  children: ReactNode;
  rendersBannerAd: boolean;
  // rendersSkinsAd: boolean; will be used in future
}) => {
  const { pageUrlChanged } = useContext(PageChangeContext);
  const [bannerAdReady, setBannerAdReady] = useState(false);
  const hasInit = useRef(false);

  useEffect(() => {
    if (pageUrlChanged) {
      if (!rendersBannerAd || bannerAdReady) {
        DataDogVitals['AdDomBannerNav'].stop();
        /** Banner component has dom element  */
        /** Clear fuseTags to restart getting ads when new components are built */
        const fusetagLib = getFuseTag();

        /** We a timeout here as we need to give the ad components time to update their attributes before refreshing */
        fusetagLib.que.push(() => {
          fusetagLib.pageInit();
        });
      }
    } else {
      if (hasInit.current) {
        /** Page has not changed so first load */
        if (!rendersBannerAd || bannerAdReady) {
          DataDogVitals['AdDomBannerInit'].stop();
        }
      }
    }
    hasInit.current = true;
    /** Page has changed reset state */
    return () => {
      setBannerAdReady(false);
    };
  }, [pageUrlChanged, bannerAdReady, rendersBannerAd]);

  return (
    <AdPageInitContext.Provider
      value={{
        bannerAdReady,
        setBannerAdReady,
      }}
    >
      {children}
    </AdPageInitContext.Provider>
  );
};
